import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Skeleton } from "antd";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  clauseMatches,
  normalizeLocateText,
  splitRefParts,
  type LocateResult,
  type LocateTarget,
  type ViewerHandle,
} from "../../types/viewerLocate";
import { revealLocated } from "../../utils/locateHighlight";
import { PreviewPageNav } from "./PreviewPageNav";

export interface PdfViewerProps {
  url: string;
  onReady?: () => void;
  onError?: (err: Error) => void;
}

interface PdfViewport {
  width: number;
  height: number;
}

interface PdfPageProxy {
  getViewport: (opts: { scale: number }) => PdfViewport;
  render: (opts: {
    canvasContext: CanvasRenderingContext2D;
    viewport: PdfViewport;
  }) => { promise: Promise<void> };
  getTextContent: () => Promise<{ items: { str: string }[] }>;
}

interface PdfDocumentProxy {
  numPages: number;
  getPage: (n: number) => Promise<PdfPageProxy>;
  /** Release the document's worker resources. */
  destroy: () => Promise<void>;
}

interface PdfDocumentParams {
  url: string;
  /** CJK CID 字体码位→Unicode 映射资源根（pdfjs-dist/cmaps/）。 */
  cMapUrl: string;
  /** .bcmap 是 packed 二进制格式，必须显式开启。 */
  cMapPacked: boolean;
  /** 14 标准字体 fallback 资源根（pdfjs-dist/standard_fonts/）。 */
  standardFontDataUrl: string;
  /**
   * 软失败开关（v5 字段名为 stopAtErrors，反向语义；false = ignore errors，
   * 字体 OTS sanitize 失败时降级为"渲染页面但字形缺失"，不中断整页渲染）。
   * pdf.js 的默认值本就是 false；此处显式声明默认软失败语义，不依赖隐式默认值。
   */
  stopAtErrors: boolean;
}

interface PdfjsModule {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument: (src: string | PdfDocumentParams) => {
    promise: Promise<PdfDocumentProxy>;
  };
}

const RENDER_SCALE = 1.2;
const RENDER_ROOT_MARGIN = "200px";

const PdfViewer = forwardRef<ViewerHandle, PdfViewerProps>(function PdfViewer(
  { url, onReady, onError },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  // The single loaded document, reused for every page render. Held in a ref so
  // it survives re-renders without re-triggering the IntersectionObserver effect.
  const docRef = useRef<PdfDocumentProxy | null>(null);
  // Pages already rendered, tracked in a ref so completing a page does NOT
  // rebuild the observer (a state Set here caused the observer to tear down and
  // re-observe on every page, re-parsing the whole document).
  const renderedRef = useRef<Set<number>>(new Set());
  // Page-number → joined page text, populated lazily by locate() so repeated
  // lookups don't re-walk the document.
  const pageTextsRef = useRef<Map<number, string>>(new Map());
  const [error, setError] = useState<Error | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  // 1-based page currently at the top of the viewport, for the page-nav footer.
  const [currentPage, setCurrentPage] = useState(1);
  // Pages whose render threw, surfaced as a per-page error overlay with retry.
  const [failedPages, setFailedPages] = useState<Set<number>>(new Set());

  const scrollToPage = useCallback((pageNum: number): boolean => {
    const canvas = pageRefs.current.get(pageNum);
    if (!canvas) return false;
    canvas.scrollIntoView({ behavior: "smooth", block: "start" });
    return true;
  }, []);

  /** Clamp + jump to a 1-based page (used by the page-nav footer). */
  const jumpToPage = useCallback(
    (page: number) => {
      const target = Math.max(1, Math.min(numPages, Math.floor(page) || 1));
      if (scrollToPage(target)) setCurrentPage(target);
    },
    [numPages, scrollToPage],
  );

  useImperativeHandle(
    ref,
    (): ViewerHandle => ({
      async locate(target: LocateTarget): Promise<LocateResult> {
        const doc = docRef.current;
        if (!doc) return "not-ready";

        // Scroll the page canvas into view AND outline its wrapper (the per-page
        // region) so the lawyer sees which page a citation landed on, not just a
        // silent scroll. setCurrentPage keeps the page-nav footer in sync.
        const revealPage = (pageNum: number): LocateResult => {
          const canvas = pageRefs.current.get(pageNum);
          if (!canvas) return "not-found";
          revealLocated(canvas.parentElement, "start");
          setCurrentPage(pageNum);
          return "ok";
        };

        if (target.page != null) {
          // Jump only when that page actually exists; an out-of-range page
          // (e.g. a finding whose page metadata overshoots the doc) falls
          // through to text matching instead of silently clamping.
          if (revealPage(target.page) === "ok") return "ok";
        }

        // Lazily load + cache a page's normalized text. Shared by the
        // clauseText scan and the ref fallback so neither re-walks pdfjs.
        const pageText = async (pageNum: number): Promise<string> => {
          const cached = pageTextsRef.current.get(pageNum);
          if (cached !== undefined) return cached;
          try {
            const page = await doc.getPage(pageNum);
            const content = await page.getTextContent();
            const text = normalizeLocateText(
              content.items.map((item) => item.str).join(""),
            );
            pageTextsRef.current.set(pageNum, text);
            return text;
          } catch {
            pageTextsRef.current.set(pageNum, "");
            return "";
          }
        };

        // 1. clauseText — AI-quoted excerpt matched per page (whole-page join,
        // so a clause spanning pdfjs text items on one page still matches).
        const clause = target.clauseText?.trim();
        if (clause) {
          for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
            if (clauseMatches(await pageText(pageNum), clause)) {
              return revealPage(pageNum);
            }
          }
        }

        // 2. ref fallback — the section the finding is about. Land on the first
        // page whose text mentions any candidate heading. Best-effort region
        // pointer when the clauseText was stitched/abridged and matched no page.
        const refParts = splitRefParts(target.ref);
        if (refParts.length) {
          for (let pageNum = 1; pageNum <= doc.numPages; pageNum += 1) {
            const text = await pageText(pageNum);
            if (refParts.some((part) => text.includes(part))) {
              return revealPage(pageNum);
            }
          }
        }

        return "not-found";
      },
    }),
    [],
  );

  /** Render a single page from the cached document. Idempotent per page. */
  const renderPage = useCallback(async (pageNum: number) => {
    const doc = docRef.current;
    if (!doc || renderedRef.current.has(pageNum)) return;
    try {
      const page = await doc.getPage(pageNum);
      const canvas = pageRefs.current.get(pageNum);
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      renderedRef.current.add(pageNum);
      setFailedPages((prev) => {
        if (!prev.has(pageNum)) return prev;
        const next = new Set(prev);
        next.delete(pageNum);
        return next;
      });
    } catch {
      // Surface the failure on this page only; the rest of the document still
      // renders. The overlay offers a manual retry.
      setFailedPages((prev) => {
        const next = new Set(prev);
        next.add(pageNum);
        return next;
      });
    }
  }, []);

  // Load the document once per url; reset render bookkeeping and destroy the
  // previous document so switching files does not leak worker memory.
  useEffect(() => {
    let cancelled = false;
    let localDoc: PdfDocumentProxy | null = null;
    renderedRef.current = new Set();
    pageTextsRef.current = new Map();
    setFailedPages(new Set());
    setNumPages(0);
    setCurrentPage(1);
    setError(null);
    (async () => {
      try {
        const pdfjs = (await import("pdfjs-dist")) as unknown as PdfjsModule;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        // CJK 渲染强制配置（pdfjs 官方文档要求）：
        //   - cMapUrl + cMapPacked：CID 字体码位→Unicode 映射，缺失则中文渲染为方块
        //     或 OTS sanitizer 拒绝整张字体表
        //   - standardFontDataUrl：14 标准字体的 fallback 资源
        //   - stopAtErrors=false：字体边缘场景软失败（pdfjs #13381 + PR #20427
        //     在 v5.4.449+ 提供 FontFaceObject setter 回写，使 OTS 修复能
        //     回写共享 ArrayBuffer；这里继续保留软失败语义），避免整页渲染中断
        // 资源经 viteStaticCopy 从 pdfjs-dist/cmaps、standard_fonts 复制到 dev server
        // 与 build 产物的 pdfjs-assets/ 下（见 apps/jerry/vite.config.ts）；用
        // BASE_URL 拼接而非硬编码根路径 "/"，使子路径部署（非空 base）不受影响。
        const assetBase = `${import.meta.env.BASE_URL}pdfjs-assets`;
        const doc = await pdfjs.getDocument({
          url,
          cMapUrl: `${assetBase}/cmaps/`,
          cMapPacked: true,
          standardFontDataUrl: `${assetBase}/standard_fonts/`,
          // v5 字段名：stopAtErrors（反向语义，false=软失败）。等价于 v4 ignoreErrors: true。
          stopAtErrors: false,
        }).promise;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        localDoc = doc;
        docRef.current = doc;
        setNumPages(doc.numPages);
        onReady?.();
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        onError?.(err);
      }
    })();
    return () => {
      cancelled = true;
      docRef.current = null;
      void localDoc?.destroy();
    };
  }, [url, onReady, onError]);

  // Observe page placeholders and lazily render those scrolled into view.
  // Depends only on numPages + the stable renderPage callback, so it is built
  // once per document instead of on every page render.
  useEffect(() => {
    if (!numPages) return;
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pageNum = Number((entry.target as HTMLElement).dataset.pageNum);
          if (pageNum) void renderPage(pageNum);
        }
      },
      { root: container, rootMargin: RENDER_ROOT_MARGIN },
    );

    const targets = container.querySelectorAll<HTMLElement>("[data-page-num]");
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [numPages, renderPage]);

  // Track the topmost visible page on scroll, for the page-nav footer. Uses
  // rAF throttling so a long doc doesn't recompute on every scroll event.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !numPages) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const top = container.getBoundingClientRect().top;
        let current = 1;
        for (let p = 1; p <= numPages; p += 1) {
          const canvas = pageRefs.current.get(p);
          if (!canvas) continue;
          // A page counts as "current" once its top has scrolled past the
          // top edge + a small threshold (so the number flips as the next
          // page enters view).
          if (canvas.getBoundingClientRect().top - top <= 48) {
            current = p;
          } else {
            break;
          }
        }
        setCurrentPage((prev) => (prev === current ? prev : current));
      });
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [numPages]);

  if (error) {
    return <div role="alert">PDF 加载失败：{error.message}</div>;
  }

  return (
    <div
      style={{
        // Outer column: scrollable page area + fixed page-nav footer. The
        // parent .detailCenter clamps the overall height.
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        background: "var(--jerry-gray-100)",
      }}
    >
      <div
        ref={containerRef}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {numPages === 0 && (
        <div role="status" aria-live="polite" style={{ position: "relative" }}>
          <Skeleton active paragraph={{ rows: 8 }} />
          <span style={{ position: "absolute", left: -9999, top: 0 }}>
            加载中
          </span>
        </div>
      )}
      {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
        <div
          key={pageNum}
          style={{ position: "relative", margin: "12px auto", width: "fit-content" }}
        >
          <canvas
            data-page-num={pageNum}
            ref={(el) => {
              if (el) pageRefs.current.set(pageNum, el);
            }}
            style={{
              display: "block",
              background: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              minWidth: 200,
              minHeight: 40,
            }}
          />
          {failedPages.has(pageNum) && (
            <div
              role="alert"
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: "rgba(255,255,255,0.85)",
                color: "var(--jerry-danger)",
                fontSize: 13,
              }}
            >
              <span>第 {pageNum} 页渲染失败</span>
              <Button size="small" onClick={() => void renderPage(pageNum)}>
                重试
              </Button>
            </div>
          )}
        </div>
      ))}
      </div>
      <PreviewPageNav current={currentPage} total={numPages} onJump={jumpToPage} />
    </div>
  );
});

export default PdfViewer;
